/**
 * Pure amount-string reducer behind the in-sheet number pad.
 *
 * The amount is held as a *string* while it is being typed, exactly as the
 * plain text input held it before, so the value handed to the submit path is
 * byte-identical for any given sequence of taps. Every function here is pure
 * and has no React or DOM dependency.
 *
 * Money is paise everywhere in this app; the POST body still carries rupees
 * (the service layer does `Math.round(amount * 100)`), so `parseAmountRupees`
 * is the submit path and `amountToPaise` mirrors the server conversion so the
 * decimal handling is provable in a unit test.
 */

/** Digits, the decimal separator, and the two editing keys. */
export type PadKey =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "decimal"
  | "backspace"
  | "clear";

/** Rupee amounts are stored as paise integers — two decimal places, no more. */
export const MAX_DECIMALS = 2;

/**
 * Upper bound on the integer part. Not a validation rule (the previous text
 * input had none) — a guard so a stuck key cannot grow the string without
 * bound. Twelve digits is ~₹1 lakh crore, far beyond any real entry.
 */
export const MAX_INTEGER_DIGITS = 12;

const DIGITS = new Set(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]);

function isDigit(key: PadKey): boolean {
  return DIGITS.has(key);
}

/** Apply one pad key to the current amount string. */
export function applyPadKey(value: string, key: PadKey): string {
  if (key === "clear") return "";
  if (key === "backspace") return value.slice(0, -1);

  if (key === "decimal") {
    if (value.includes(".")) return value;
    // A bare "." is never a valid amount, so seed the zero for the user.
    return value === "" ? "0." : `${value}.`;
  }

  if (!isDigit(key)) return value;

  const dotAt = value.indexOf(".");

  if (dotAt === -1) {
    // Integer part. A lone leading zero is a placeholder for "0.xx" — any
    // following digit replaces it rather than producing "00" or "05".
    if (value === "0") return key === "0" ? "0" : key;
    if (value.length >= MAX_INTEGER_DIGITS) return value;
    return value + key;
  }

  const decimals = value.length - dotAt - 1;
  if (decimals >= MAX_DECIMALS) return value;
  return value + key;
}

/**
 * Map a physical-keyboard `KeyboardEvent.key` onto a pad key, so a desktop
 * user drives the same reducer the pad does. Returns null for keys the pad
 * does not own (Enter, Escape, Tab, arrows, letters) — the caller must let
 * those through untouched.
 */
export function padKeyFromKeyboard(key: string): PadKey | null {
  if (DIGITS.has(key)) return key as PadKey;
  if (key === "." || key === ",") return "decimal";
  if (key === "Backspace" || key === "Delete") return "backspace";
  return null;
}

/**
 * Coerce arbitrary pasted text into a value the pad could itself have
 * produced: drops the currency symbol, grouping commas, spaces, sign and any
 * letters, keeps the first separator only, and clamps to two decimals.
 */
export function sanitizeAmountInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dotAt = cleaned.indexOf(".");

  let intPart = dotAt === -1 ? cleaned : cleaned.slice(0, dotAt);
  const hasDecimals = dotAt !== -1;
  let decPart = hasDecimals ? cleaned.slice(dotAt + 1).replace(/\./g, "") : "";

  intPart = intPart.replace(/^0+(?=\d)/, "");
  if (intPart === "") intPart = hasDecimals ? "0" : "";
  if (intPart.length > MAX_INTEGER_DIGITS) intPart = intPart.slice(0, MAX_INTEGER_DIGITS);

  if (!hasDecimals) return intPart;
  decPart = decPart.slice(0, MAX_DECIMALS);
  return `${intPart}.${decPart}`;
}

/**
 * The submit path. Returns the positive rupee value, or null when the string
 * is not a submittable amount — empty, a bare separator, or zero. Never NaN.
 *
 * Deliberately identical to the previous inline
 * `const v = parseFloat(amount); Number.isFinite(v) && v > 0` check, including
 * its tolerance of a trailing separator ("1." → 1).
 */
export function parseAmountRupees(value: string): number | null {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Paise for a typed amount — mirrors `Math.round(data.amount * 100)` in the
 * income/expense services. Rounding is what keeps 8.20 at 820 rather than the
 * 819.9999999999999 that raw float multiplication yields.
 */
export function amountToPaise(value: string): number | null {
  const rupees = parseAmountRupees(value);
  return rupees === null ? null : Math.round(rupees * 100);
}

/* ===========================================================================
   Calculator layer
   ===========================================================================

   A thin, still-pure state machine wrapped *around* `applyPadKey` — the digit
   grammar is not reimplemented, it is delegated to. The only thing added is
   an accumulator and a pending operator.

   Two rules, both unit-tested, both load-bearing in an accounting app:

   1. Arithmetic runs in **integer paise**. `entryToPaise` reads the digits out
      of the string rather than multiplying a float, so `0.1 + 0.2` is 30 paise
      rather than 0.30000000000000004 rupees. `+` and `-` are therefore exact;
      `×` and `÷` round exactly once, with `Math.round`, the same operation the
      income/expense services apply at the boundary.

   2. Evaluation is **strict left-to-right — no operator precedence**. `2 + 3 ×
      4` is 20, not 14. This is what every phone and pocket calculator does,
      it is what the Wallet reference does, and for the thing this pad is
      actually for (adding up a handful of amounts) it is the correct answer.
*/

export type OperatorKey = "add" | "subtract" | "multiply" | "divide";

/** Everything the calculator pad can send: pad keys plus arithmetic. */
export type CalcKey = PadKey | OperatorKey | "equals";

export interface CalcState {
  /** The number currently being typed — same grammar as `applyPadKey`. */
  entry: string;
  /** Left-hand operand in exact paise, or null when no operation is pending. */
  pendingPaise: number | null;
  /** The operator waiting for its right-hand operand. */
  op: OperatorKey | null;
  /** Set by `=`, so the next digit starts a fresh entry instead of appending. */
  justEvaluated: boolean;
}

export const INITIAL_CALC_STATE: CalcState = {
  entry: "",
  pendingPaise: null,
  op: null,
  justEvaluated: false,
};

/** The glyph printed on each operator key (and in the pending-expression line). */
export const OPERATOR_SYMBOL: Record<OperatorKey, string> = {
  add: "+",
  subtract: "−", // U+2212 minus, not a hyphen
  multiply: "×",
  divide: "÷",
};

const OPERATORS = new Set<string>(["add", "subtract", "multiply", "divide"]);

export function isOperatorKey(key: CalcKey): key is OperatorKey {
  return OPERATORS.has(key);
}

/**
 * Exact paise for an amount string, read digit by digit — never
 * `parseFloat(v) * 100`.
 *
 * Differs from `amountToPaise` in two deliberate ways, because this is the
 * *arithmetic* path rather than the submit path: zero is a legitimate operand,
 * and a negative string (which subtraction can produce) converts rather than
 * being rejected. Returns null only when there is no digit at all.
 */
export function entryToPaise(value: string): number | null {
  const m = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(value);
  if (!m) return null;
  const [, sign, intPart, decPart] = m;
  if (intPart === "" && (decPart === undefined || decPart === "")) return null;
  const paise = Number(intPart || "0") * 100 + Number(((decPart ?? "") + "00").slice(0, 2));
  return sign === "-" ? -paise : paise;
}

/**
 * The inverse: renders paise back into the amount-string grammar, so a result
 * can be typed on top of, backspaced into, and submitted through exactly the
 * same path a hand-typed amount takes. Whole rupees carry no decimals.
 */
export function paiseToEntry(paise: number): string {
  const neg = paise < 0;
  const abs = Math.abs(Math.trunc(paise));
  const rupees = Math.floor(abs / 100);
  const rem = abs % 100;
  const body = rem === 0 ? String(rupees) : `${rupees}.${String(rem).padStart(2, "0")}`;
  return neg ? `-${body}` : body;
}

/**
 * One arithmetic step, in paise. Returns null only for division by zero —
 * the caller treats that as "do nothing" rather than surfacing Infinity or
 * NaN into a field that will be saved.
 *
 * `×` and `÷` take a paise-denominated right operand, so the /100 and ×100
 * cancel the extra scale factor; each rounds exactly once.
 */
export function computePaise(a: number, op: OperatorKey, b: number): number | null {
  switch (op) {
    case "add":
      return a + b;
    case "subtract":
      return a - b;
    case "multiply":
      return Math.round((a * b) / 100);
    case "divide":
      return b === 0 ? null : Math.round((a * 100) / b);
  }
}

/** Apply one calculator key. Pure; digits delegate to `applyPadKey`. */
export function applyCalcKey(state: CalcState, key: CalcKey): CalcState {
  if (key === "clear") return INITIAL_CALC_STATE;

  if (key === "backspace") {
    return {
      ...state,
      entry: applyPadKey(state.entry, "backspace"),
      justEvaluated: false,
    };
  }

  if (key === "equals") {
    if (state.op === null || state.pendingPaise === null) return state;
    // `5 + =` repeats the operand, the pocket-calculator convention.
    const rhs = state.entry === "" ? state.pendingPaise : entryToPaise(state.entry);
    const result = computePaise(state.pendingPaise, state.op, rhs ?? state.pendingPaise);
    if (result === null) return state; // division by zero — change nothing
    return {
      entry: paiseToEntry(result),
      pendingPaise: null,
      op: null,
      justEvaluated: true,
    };
  }

  if (isOperatorKey(key)) {
    // A pending operation resolves the moment the next operator arrives —
    // that is what makes the chain left-to-right and shows a running total.
    if (state.op !== null && state.pendingPaise !== null && state.entry !== "") {
      const rhs = entryToPaise(state.entry) ?? 0;
      const result = computePaise(state.pendingPaise, state.op, rhs);
      if (result === null) return state; // division by zero — change nothing
      return { entry: "", pendingPaise: result, op: key, justEvaluated: false };
    }
    const lhs = state.entry !== "" ? entryToPaise(state.entry) : state.pendingPaise;
    return { entry: "", pendingPaise: lhs ?? 0, op: key, justEvaluated: false };
  }

  // Digit or separator: after `=` the next keystroke begins a new number
  // rather than extending the result.
  const base = state.justEvaluated ? "" : state.entry;
  return { ...state, entry: applyPadKey(base, key), justEvaluated: false };
}

/**
 * Settles a half-typed expression. `250 + 130` with `=` never pressed still
 * saves 380 — the alternative is silently dropping the tail of what the user
 * typed, which in an accounting app is the worst possible failure mode.
 */
export function resolveCalcState(state: CalcState): CalcState {
  if (state.op === null || state.pendingPaise === null) return state;
  // A DANGLING operator (`83.76 × 9 +`, typed to see the running total) is
  // dropped, never applied. Pressing `=` on a pocket calculator repeats the
  // operand, but SAVING is not pressing `=`: applying that convention here
  // turned 753.84 into 1,507.68 on the way to the server (found 2026-09-29).
  if (state.entry === "") {
    return { entry: paiseToEntry(state.pendingPaise), pendingPaise: null, op: null, justEvaluated: true };
  }
  return applyCalcKey(state, "equals");
}

/**
 * The live result while an operation is pending — `= 753.84` under the figure,
 * so nobody has to press an operator just to see the answer. Empty when there
 * is nothing to resolve.
 */
export function calcResultLabel(state: CalcState): string {
  if (state.op === null || state.pendingPaise === null || state.entry === "") return "";
  const settled = resolveCalcState(state);
  return `= ${formatAmountForDisplay(settled.entry)}`;
}

/**
 * The small line above the figure: `1,234 +` while an operand is awaited.
 * Empty when nothing is pending, so no space is spent on an idle row.
 */
export function calcPendingLabel(state: CalcState): string {
  if (state.op === null || state.pendingPaise === null) return "";
  return `${formatAmountForDisplay(paiseToEntry(state.pendingPaise))} ${OPERATOR_SYMBOL[state.op]}`;
}

/** Indian digit grouping: last three, then twos (1,23,45,678). */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  return `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${last3}`;
}

/**
 * Presentation only — groups the integer part and leaves the decimals (and a
 * trailing separator mid-typing) exactly as typed. Stripping the commas
 * always returns the original string.
 */
export function formatAmountForDisplay(value: string): string {
  if (value === "") return "";
  const dotAt = value.indexOf(".");
  if (dotAt === -1) return groupIndian(value);
  return `${groupIndian(value.slice(0, dotAt))}.${value.slice(dotAt + 1)}`;
}
